import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { clearOnline, errorText, getGame, getOnline } from "@/lib/api";

const POLL_EVERY = 1000;
const TABLE_CODES = [401, 403, 404, 409];

// Online mode for /game/:gameId: version-guarded state, ~1 s polling and the spec's error table.
export function useOnlineGame(gameId, setGame) {
  const navigate = useNavigate();
  const online = useMemo(() => getOnline()?.game_id === gameId, [gameId]);
  const [viewer, setViewer] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);
  const versionRef = useRef(null);
  // TERRAIN 1 of 3: a save from before terrain heights is refused (410 old_version) -> stop polling.
  const [oldVersion, setOldVersion] = useState(null);
  const oldRef = useRef(false);

  const mergeViewer = useCallback((v) => {
    setViewer((prev) => (prev && JSON.stringify(prev) === JSON.stringify(v) ? prev : v));
  }, []);

  // Pass & Play: plain setGame. Online: drop anything not newer than what's on screen.
  const accept = useCallback(
    (g) => {
      if (!online) {
        setGame(g);
        return;
      }
      if (!g || (versionRef.current !== null && g.version <= versionRef.current)) return;
      versionRef.current = g.version;
      mergeViewer(g.viewer);
      setGame(g);
    },
    [online, setGame, mergeViewer],
  );

  const reloadRef = useRef(null);
  const fail = useCallback(
    (e, fallback, { quiet = false } = {}) => {
      const res = e?.response;
      if (!res) {
        setReconnecting(true);
        return;
      }
      if (res.status === 401) {
        const code = res.data?.detail?.lobby_code;
        clearOnline();
        navigate(code ? `/join/${code}` : "/");
        return;
      }
      if (res.status === 410 && res.data?.detail?.code === "old_version") {
        oldRef.current = true;
        clearOnline();
        setOldVersion(res.data.detail);
        return;
      }
      if (res.status === 404) {
        clearOnline();
        toast("This game has ended");
        navigate("/");
        return;
      }
      if (quiet) return;
      if (res.status === 409) {
        toast("Someone moved first", { duration: 1800 });
        reloadRef.current?.();
        return;
      }
      toast.error(errorText(e, fallback));
    },
    [navigate],
  );

  const reload = useCallback(async () => {
    try {
      const g = await getGame(gameId);
      setReconnecting(false);
      accept(g);
    } catch (e) {
      fail(e, "Could not load the game", { quiet: true });
    }
  }, [gameId, accept, fail]);
  reloadRef.current = reload;

  useEffect(() => {
    if (!online) return undefined;
    let stopped = false;
    let inFlight = false;
    const id = setInterval(async () => {
      if (inFlight || oldRef.current) return;
      inFlight = true;
      try {
        const r = await getGame(gameId, versionRef.current);
        if (stopped) return;
        setReconnecting(false);
        if (r.changed === false) {
          if (r.version === versionRef.current) mergeViewer(r.viewer);
        } else {
          accept(r);
        }
      } catch (e) {
        if (!stopped) fail(e, null, { quiet: true });
      } finally {
        inFlight = false;
      }
    }, POLL_EVERY);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [online, gameId, accept, fail, mergeViewer]);

  // True when an online error belongs to the status table (not a plain rule error).
  const isTableError = useCallback(
    (e) => online && (!e?.response || TABLE_CODES.includes(e.response.status)),
    [online],
  );

  return { online, viewer, reconnecting, accept, fail, reload, isTableError, oldVersion, setOldVersion };
}
